import { useCallback, useMemo, useState } from "react";

import { CoveragePlaceholderCard } from "@components/Cards/CoveragePlaceholderCard/CoveragePlaceholderCard";
import { coverageDefinition } from "@components/Cards/CoveragePlaceholderCard/coverageDefinition";
import { RecentAlertsList } from "@components/Cards/RecentAlertsCard/RecentAlertsCard";
import { useRecentAlerts } from "@components/Cards/RecentAlertsCard/useRecentAlerts";
import {
  ApplicationReviewStateMixCard,
  NewAppsDiscoveryTrendChart,
} from "@components/Charts";
import {
  DashboardCell,
  DashboardGrid,
  DashboardRangeControl,
  DashboardWidget,
  dashboardRangeEndingToday,
  type DashboardDateRange,
} from "@components/Dashboards/shell";

import { Clickable } from "@design-system/Clickable/Clickable";
import { Text } from "@design-system/Text/Text";

type Props = {
  clientId: string;
};

export const ClientDashboard = ({ clientId }: Props) => {
  const alerts = useRecentAlerts(clientId);
  const [discoveryRange, setDiscoveryRange] = useState<DashboardDateRange>(
    () => dashboardRangeEndingToday(90),
  );
  const [discoverySummary, setDiscoverySummary] = useState({
    totalCount: 0,
    isLoading: true,
  });
  const handleDiscoverySummary = useCallback(
    (summary: { totalCount: number; isLoading: boolean }) => {
      setDiscoverySummary(summary);
    },
    [],
  );

  const discoveryDefinition = useMemo(() => {
    if (discoverySummary.isLoading) {
      return undefined;
    }
    const count = discoverySummary.totalCount;
    return `${count} new application${count === 1 ? "" : "s"}`;
  }, [discoverySummary]);

  return (
    <DashboardGrid>
      <DashboardCell span={12}>
        <ApplicationReviewStateMixCard clientId={clientId} />
      </DashboardCell>

      <DashboardCell span={8}>
        <DashboardWidget
          title="Recent alerts"
          definition={
            alerts.isLoading ? undefined : `${alerts.totalCount} unresolved`
          }
          actions={
            alerts.totalCount > 0 ? (
              <Clickable onClick={alerts.handleSeeAll}>
                <Text size={12} color="primary" weight={600}>
                  See all
                </Text>
              </Clickable>
            ) : null
          }
        >
          <RecentAlertsList clientId={clientId} />
        </DashboardWidget>
      </DashboardCell>
      <DashboardCell span={4}>
        <DashboardWidget
          title="Coverage"
          definition={coverageDefinition("tenant")}
        >
          <CoveragePlaceholderCard variant="tenant" />
        </DashboardWidget>
      </DashboardCell>

      <DashboardCell span={12}>
        <DashboardWidget
          title="New applications discovered"
          definition={discoveryDefinition}
          actions={
            <DashboardRangeControl
              value={discoveryRange}
              onChange={setDiscoveryRange}
            />
          }
        >
          <NewAppsDiscoveryTrendChart
            clientId={clientId}
            dateRange={discoveryRange}
            onSummaryChange={handleDiscoverySummary}
          />
        </DashboardWidget>
      </DashboardCell>
    </DashboardGrid>
  );
};
